if (!customElements.get('amare-product-page')) {
  customElements.define('amare-product-page', class extends HTMLElement {
    connectedCallback() {
      const variantData = this.querySelector('[data-amare-variants]');
      try {
        this.variants = JSON.parse(variantData?.textContent || '[]');
      } catch (error) {
        this.variants = [];
        console.error('AMARE variantdata kon niet worden gelezen.', error);
      }
      const selectedVariantId = Number(this.querySelector('product-form [name="id"]')?.value);
      const selectedVariant = this.variants.find((variant) => Number(variant.id) === selectedVariantId);
      this.productMode = this.dataset.productMode || 'eyewear';
      this.selectedColor = selectedVariant?.color || this.dataset.selectedColor || '';
      this.selectedSize = selectedVariant?.size || this.dataset.selectedSize || '';
      this.preloadedImages = new Map();

      this.querySelectorAll('[data-pdp-thumb]').forEach((button) => {
        button.addEventListener('click', () => this.selectImage(button.dataset.image, button.dataset.mediaId));
        this.warmImageOnIntent(button, () => button.dataset.image);
      });
      this.querySelectorAll('[data-pdp-variant]').forEach((button) => {
        button.addEventListener('click', () => this.selectVariant(Number(button.dataset.pdpVariant)));
        this.warmImageOnIntent(button, () => {
          const variant = this.variants.find((item) => Number(item.id) === Number(button.dataset.pdpVariant));
          return variant?.image;
        });
      });
      this.querySelectorAll('[data-pdp-shoe-color]').forEach((button) => {
        button.addEventListener('click', () => this.selectShoeOption('color', button.dataset.pdpShoeColor));
        this.warmImageOnIntent(button, () => {
          const variant = this.variants.find(
            (item) => item.color === button.dataset.pdpShoeColor && item.available
          );
          return variant?.image;
        });
      });
      this.querySelectorAll('[data-pdp-shoe-size]').forEach((button) => {
        button.addEventListener('click', () => this.selectShoeOption('size', button.dataset.pdpShoeSize));
      });

      const open = this.querySelector('[data-amare-zoom-open]');
      const close = this.querySelector('[data-amare-zoom-close]');
      const dialog = this.querySelector('[data-amare-zoom]');
      open?.addEventListener('click', () => this.openZoom());
      close?.addEventListener('click', () => this.closeZoom());
      this.querySelector('[data-amare-zoom-previous]')?.addEventListener('click', () => this.navigateZoom(-1));
      this.querySelector('[data-amare-zoom-next]')?.addEventListener('click', () => this.navigateZoom(1));
      this.querySelector('[data-amare-zoom-toggle]')?.addEventListener('click', () => this.toggleZoom());
      this.querySelectorAll('[data-amare-zoom-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          const source = this.getMediaSource(button.dataset.mediaId);
          this.selectImage(source?.dataset.image, button.dataset.mediaId);
          this.showZoomMedia(button.dataset.mediaId);
        });
      });
      dialog?.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeZoom();
      });
      dialog?.addEventListener('close', () => this.resetZoomScale());
      dialog?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigateZoom(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigateZoom(1);
        }
      });

      this.filterMedia(this.dataset.selectedColor || '');
      if (selectedVariant && this.productMode === 'shoes') this.updateShoeControls(selectedVariant);
      this.preloadVariantImages();
    }

    preloadImage(src) {
      if (!src || this.preloadedImages.has(src)) return this.preloadedImages.get(src);
      const image = new Image();
      image.decoding = 'async';
      image.sizes = '(min-width: 1025px) 58vw, 100vw';
      image.srcset = this.getImageSrcset(src);
      image.src = src;
      this.preloadedImages.set(src, image);
      return image;
    }

    getImageSrcset(src) {
      return [640, 900, 1200, 1600]
        .map((width) => `${src.replace(/([?&])width=\d+/, `$1width=${width}`)} ${width}w`)
        .join(', ');
    }

    warmImageOnIntent(element, resolveSource) {
      const warm = () => this.preloadImage(resolveSource());
      element.addEventListener('pointerenter', warm, { once: true });
      element.addEventListener('pointerdown', warm, { once: true });
      element.addEventListener('focus', warm, { once: true });
    }

    preloadVariantImages() {
      const preload = () => {
        new Set(this.variants.map((variant) => variant.image).filter(Boolean)).forEach((src) => {
          this.preloadImage(src);
        });
      };

      if (document.readyState === 'complete') {
        window.setTimeout(preload, 0);
      } else {
        window.addEventListener('load', preload, { once: true });
      }
    }

    selectShoeOption(option, value) {
      if (option === 'color') this.selectedColor = value;
      if (option === 'size') this.selectedSize = value;

      let variant = this.variants.find(
        (item) => item.color === this.selectedColor && String(item.size) === String(this.selectedSize)
      );
      if ((!variant || !variant.available) && option === 'color') {
        variant = this.variants.find((item) => item.color === this.selectedColor && item.available);
      }
      if (variant) this.selectVariant(variant.id);
    }

    updateShoeControls(variant) {
      this.selectedColor = variant.color;
      this.selectedSize = variant.size;
      this.querySelectorAll('[data-pdp-shoe-color]').forEach((button) => {
        const active = button.dataset.pdpShoeColor === variant.color;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-pdp-shoe-size]').forEach((button) => {
        const size = button.dataset.pdpShoeSize;
        const available = this.variants.some(
          (item) => item.color === variant.color && String(item.size) === String(size) && item.available
        );
        const active = String(size) === String(variant.size);
        const stock = button.querySelector('.amare-pdp__size-stock');
        button.disabled = !available;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-label', `EU ${size} — ${available ? this.dataset.labelInStock : this.dataset.labelSoldOut}`);
        stock?.classList.toggle('is-unavailable', !available);
      });
      const size = this.querySelector('[data-pdp-size]');
      const sku = this.querySelector('[data-pdp-sku]');
      if (size) size.textContent = variant.size;
      if (sku) sku.textContent = String(variant.sku || '').split(':')[0];
    }

    filterMedia(colorKey) {
      const thumbnails = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
      thumbnails.forEach((thumb) => {
        const mediaColor = thumb.dataset.mediaColor || '';
        thumb.hidden = Boolean(mediaColor && colorKey && mediaColor !== colorKey);
      });
      this.querySelectorAll('[data-amare-zoom-thumb]').forEach((thumb) => {
        const mediaColor = thumb.dataset.mediaColor || '';
        thumb.hidden = Boolean(mediaColor && colorKey && mediaColor !== colorKey);
      });

      const active = thumbnails.find((thumb) => thumb.classList.contains('is-active'));
      if (active?.hidden) {
        const firstVisible = thumbnails.find((thumb) => !thumb.hidden);
        if (firstVisible) this.selectImage(firstVisible.dataset.image, firstVisible.dataset.mediaId);
      }
      this.updateZoomNavigation();
    }

    selectImage(src, mediaId) {
      const image = this.querySelector('.amare-pdp__main-image');
      if (!image || !src) return;
      this.preloadImage(src);
      image.sizes = '(min-width: 1025px) 58vw, 100vw';
      image.srcset = this.getImageSrcset(src);
      image.src = src;
      this.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.currentMediaId = String(mediaId);
      this.showZoomMedia(mediaId);
    }

    selectVariant(id) {
      const variant = this.variants.find((item) => Number(item.id) === Number(id));
      if (!variant) return;

      this.querySelectorAll('[data-pdp-variant]').forEach((button) => {
        const active = Number(button.dataset.pdpVariant) === Number(id);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const color = this.querySelector('[data-pdp-color]');
      const price = this.querySelector('[data-pdp-price]');
      const compare = this.querySelector('[data-pdp-compare]');
      const variantInput = this.querySelector('product-form [name="id"]');
      const submit = this.querySelector('product-form [type="submit"]');
      const stock = this.querySelector('[data-pdp-stock-dot]');
      const deliveryTitle = this.querySelector('[data-pdp-delivery-title]');
      const deliveryText = this.querySelector('[data-pdp-delivery-text]');
      if (color) color.textContent = variant.color;
      if (price) price.textContent = variant.price;
      if (compare) {
        compare.textContent = variant.compareAt;
        compare.hidden = !variant.onSale;
      }
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.disabled = !variant.available;
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        const label = submit.querySelector('[data-pdp-submit]');
        if (label) {
          const availableLabel = submit.dataset.availableLabel || this.dataset.labelAdd;
          const unavailableLabel = submit.dataset.unavailableLabel || this.dataset.labelSoldOut;
          label.textContent = variant.available
            ? `${availableLabel}${this.productMode === 'shoes' ? ` — ${variant.price}` : ''}`
            : unavailableLabel;
        }
      }
      if (stock) {
        stock.classList.toggle('is-unavailable', !variant.available);
        stock.setAttribute('aria-label', variant.available ? this.dataset.labelInStock : this.dataset.labelSoldOut);
      }
      if (deliveryTitle) deliveryTitle.textContent = variant.available ? deliveryTitle.dataset.availableText : this.dataset.labelTemporarilySoldOut;
      if (deliveryText) deliveryText.textContent = variant.available ? deliveryText.dataset.availableText : this.dataset.labelChooseOther;
      if (this.productMode === 'shoes') this.updateShoeControls(variant);
      this.filterMedia(variant.colorKey || '');
      if (variant.image) this.selectImage(variant.image, variant.mediaId);
      if (variant.url) window.history.replaceState({}, '', variant.url);
    }

    openZoom() {
      const dialog = this.querySelector('[data-amare-zoom]');
      const active = this.querySelector('[data-pdp-thumb].is-active:not([hidden])') || this.querySelector('[data-pdp-thumb]:not([hidden])');
      if (!dialog || !active) return;
      this.showZoomMedia(active.dataset.mediaId);
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
        this.querySelector('[data-amare-zoom-close]')?.focus();
      }
    }

    closeZoom() {
      const dialog = this.querySelector('[data-amare-zoom]');
      if (dialog?.open) dialog.close();
    }

    getMediaSource(mediaId) {
      return Array.from(this.querySelectorAll('[data-pdp-thumb]')).find(
        (thumb) => String(thumb.dataset.mediaId) === String(mediaId)
      );
    }

    getVisibleMedia() {
      return Array.from(this.querySelectorAll('[data-pdp-thumb]')).filter((thumb) => !thumb.hidden);
    }

    showZoomMedia(mediaId) {
      const source = this.getMediaSource(mediaId);
      const zoomImage = this.querySelector('[data-amare-zoom-image]');
      if (!source || !zoomImage || source.hidden) return;

      this.currentMediaId = String(mediaId);
      zoomImage.src = source.dataset.zoomImage || source.dataset.image;
      zoomImage.alt = source.dataset.imageAlt || '';
      this.querySelectorAll('[data-amare-zoom-thumb]').forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === this.currentMediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active && this.querySelector('[data-amare-zoom]')?.open) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
      this.resetZoomScale();
      this.updateZoomNavigation();
    }

    updateZoomNavigation() {
      const visibleMedia = this.getVisibleMedia();
      const currentIndex = Math.max(0, visibleMedia.findIndex(
        (thumb) => String(thumb.dataset.mediaId) === String(this.currentMediaId)
      ));
      const counter = this.querySelector('[data-amare-zoom-counter]');
      if (counter) counter.textContent = `${visibleMedia.length ? currentIndex + 1 : 0} / ${visibleMedia.length}`;
      const disabled = visibleMedia.length < 2;
      const previous = this.querySelector('[data-amare-zoom-previous]');
      const next = this.querySelector('[data-amare-zoom-next]');
      if (previous) previous.disabled = disabled;
      if (next) next.disabled = disabled;
    }

    navigateZoom(direction) {
      const visibleMedia = this.getVisibleMedia();
      if (visibleMedia.length < 2) return;
      let currentIndex = visibleMedia.findIndex(
        (thumb) => String(thumb.dataset.mediaId) === String(this.currentMediaId)
      );
      if (currentIndex < 0) currentIndex = 0;
      const nextIndex = (currentIndex + direction + visibleMedia.length) % visibleMedia.length;
      const target = visibleMedia[nextIndex];
      this.selectImage(target.dataset.image, target.dataset.mediaId);
    }

    toggleZoom() {
      const button = this.querySelector('[data-amare-zoom-toggle]');
      if (!button) return;
      const zoomed = button.classList.toggle('is-zoomed');
      button.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      button.setAttribute('aria-label', zoomed ? this.dataset.labelZoomOut : this.dataset.labelZoomIn);
    }

    resetZoomScale() {
      const button = this.querySelector('[data-amare-zoom-toggle]');
      if (!button) return;
      button.classList.remove('is-zoomed');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', this.dataset.labelZoomIn);
    }
  });
}
